import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, deleteDataAPI, postDataAPI } from '../../utils/fetchData';
import { canDeleteContent } from '../../utils/permissions';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';

const ViewAll = () => {
    const navigate = useNavigate();
    const { modalConfig, showModal, closeModal } = useModal();
    const [activeTab, setActiveTab] = useState('all');
    const [merch, setMerch] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [showImportDropdown, setShowImportDropdown] = useState(false);
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadMerch();
    }, []);

    const loadMerch = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            console.log('🔍 MERCH LOAD DEBUG - Loading merch with token:', token);
            const response = await getDataAPI('user/getMerch', token);
            console.log('🔍 MERCH LOAD DEBUG - Merch API Response:', response);
            console.log('🔍 MERCH LOAD DEBUG - Response data:', response.data);
            
            if (response.data.status === 200 && response.data.data) {
                // Check if the data is nested deeper
                const merchData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
                console.log('🔍 MERCH LOAD DEBUG - Final merch data:', merchData);
                console.log('🔍 MERCH LOAD DEBUG - Final merch data length:', merchData?.length);
                
                if (Array.isArray(merchData)) {
                    console.log('🔍 MERCH LOAD DEBUG - Setting merch state with:', merchData.length, 'items');
                    console.log('🔍 MERCH LOAD DEBUG - Sample retrieved item:', merchData[0]);
                    setMerch(merchData);
                } else {
                    console.log('🔍 MERCH LOAD DEBUG - Merch data is not an array, setting empty array');
                    setMerch([]);
                }
            } else {
                console.log('🔍 MERCH LOAD DEBUG - Merch API response not successful:', response.data);
                console.log('🔍 MERCH LOAD DEBUG - Setting merch state to empty array');
                setMerch([]);
            }
        } catch (error) {
            console.error('🔍 MERCH LOAD DEBUG - Failed to load merch:', error);
            console.error('🔍 MERCH LOAD DEBUG - Merch error details:', error.response?.data);
            setMerch([]);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = () => {
        navigate('/');
    };

    const formatPrice = (price) => {
        if (!price) return '$0.00';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const getProductIcon = (type) => {
        // Return a simple colored square instead of emojis
        return <div className="w-6 h-6 bg-[#9932CC] rounded"></div>;
    };

    const handleEdit = (merchItem) => {
        navigate(`/merch/edit/${merchItem._id}`);
    };

    const getCurrentDisplayData = () => {
        return Array.isArray(merch) ? merch : [];
    };

    const handleItemSelect = (merchItem) => {
        setSelectedItems(prev => {
            const isSelected = prev.some(item => item._id === merchItem._id);
            if (isSelected) {
                return prev.filter(item => item._id !== merchItem._id);
            } else {
                return [...prev, merchItem];
            }
        });
    };

    const handleSelectAll = () => {
        const currentData = getCurrentDisplayData();
        const allSelected = currentData.every(merchItem => selectedItems.some(item => item._id === merchItem._id));
        
        if (allSelected) {
            // Deselect all current items
            setSelectedItems(prev => prev.filter(item => !currentData.some(merchItem => merchItem._id === item._id)));
        } else {
            // Select all current items
            const newSelections = currentData.filter(merchItem => !selectedItems.some(item => item._id === merchItem._id));
            setSelectedItems(prev => [...prev, ...newSelections]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedItems.length === 0) return;

        setIsDeleting(true);
        const token = localStorage.getItem('dizeeToken');
        let successCount = 0;
        let failCount = 0;

        for (const item of selectedItems) {
            try {
                await postDataAPI('user/deleteMerch', { id: item._id }, token);
                successCount++;
            } catch (error) {
                console.error(`Failed to delete merch item ${item.product_name}:`, error);
                failCount++;
            }
        }

        if (successCount > 0) {
            const message = `Successfully deleted ${successCount} merch item(s)${failCount > 0 ? `, ${failCount} failed` : ''}`;
            showModal('Delete Complete', message, 'success');
            setSelectedItems([]);
            loadMerch(); // Reload data
        } else {
            showModal('Delete Failed', 'Failed to delete selected merch items. Please try again.', 'error');
        }

        setIsDeleting(false);
        setShowDeleteConfirm(false);
    };

    const tabs = [
        { id: 'products', label: 'Products' },
        { id: 'orders', label: 'Orders' },
        { id: 'financials', label: 'Financials' },
        { id: 'analytics', label: 'Analytics' }
    ];

    const handleImportOption = (option) => {
        setShowImportDropdown(false);
        switch(option) {
            case 'manual':
                navigate('/merch/manual-add');
                break;
            case 'bulk':
                navigate('/merch/bulk-import');
                break;
            default:
                break;
        }
    };

    const getFilteredMerch = () => {
        const safeMerch = Array.isArray(merch) ? merch : [];
        
        if (!searchQuery.trim()) {
            return safeMerch;
        }
        
        const query = searchQuery.toLowerCase();
        return safeMerch.filter(product => 
            product.product_name?.toLowerCase().includes(query) ||
            product.vendor_brand?.toLowerCase().includes(query) ||
            product.product_type?.toLowerCase().includes(query) ||
            product.description?.toLowerCase().includes(query)
        );
    };

    const renderProducts = () => {
        const filteredProducts = getFilteredMerch();
        const products = { info: filteredProducts, variants: [] };
        console.log('🔍 MERCH RENDER DEBUG - Total merch items in state:', filteredProducts.length);
        console.log('🔍 MERCH RENDER DEBUG - Merch state data:', filteredProducts);
        console.log('🔍 MERCH RENDER DEBUG - Sample item for rendering:', filteredProducts[0]);
        
        if (products.info.length === 0) {
            return (
                <div className="text-left py-[60px]">
                    <h3 className="text-white text-[12px] font-semibold mb-2">No products found</h3>
                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                        Import your merchandise data to see all your products here.
                    </p>
                    <button 
                        onClick={() => navigate('/merch/bulk-import')}
                        className="bg-[#9932CC] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#A555CC] transition-colors"
                    >
                        Import Merch
                    </button>
                </div>
            );
        }

        return (
            <>
                {/* Select All Button */}
                {products.info.length > 0 && canDeleteContent('merch') && (
                    <div className="px-4 mb-4 text-right">
                        <button
                            onClick={handleSelectAll}
                            className="text-[#9932CC] text-[10px] hover:text-[#A555CC] transition-colors"
                        >
                            {products.info.every(product => selectedItems.some(item => item._id === product._id)) ? 
                                'Deselect All' : 'Select All'
                            }
                        </button>
                    </div>
                )}
                
                <div className="grid gap-4">
                    {products.info.map((product, index) => {
                        const isSelected = selectedItems.some(item => item._id === product._id);
                        
                        return (
                            <div 
                                key={index}
                                className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 transition-all cursor-pointer ${
                                    isSelected 
                                        ? 'border-[#9932CC] bg-[rgba(153,50,204,0.1)]' 
                                        : 'border-[rgba(255,255,255,0.1)] hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)]'
                                }`}
                                onClick={() => canDeleteContent('merch') ? handleItemSelect(product) : null}
                                style={{ cursor: canDeleteContent('merch') ? 'pointer' : 'default' }}
                            >
                                <div className="flex items-center gap-4">
                                    {/* Product Icon */}
                                    <div className="w-12 h-12 bg-[rgba(153,50,204,0.2)] rounded-lg flex items-center justify-center">
                                            {getProductIcon(product.product_type)}
                                    </div>
                                    
                                    {/* Product Info */}
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <h3 className="text-white text-[12px] font-normal">
                                                {product.product_name || 'Unknown Product'}
                                            </h3>
                                            <button
                                                onClick={() => handleEdit(product)}
                                                className="text-[#9932CC] text-[10px] px-2 py-1 hover:bg-[rgba(153,50,204,0.1)] rounded transition-colors"
                                            >
                                                Edit
                                            </button>
                                        </div>
                                        <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-2">
                                            {product.vendor_brand || 'Unknown Brand'}
                                        </p>
                                        <div className="flex items-center gap-3 text-[rgba(255,255,255,0.5)] text-[10px]">
                                            {product.product_type && (
                                                <span className="bg-[rgba(153,50,204,0.2)] px-2 py-1 rounded">
                                                    {product.product_type}
                                                </span>
                                            )}
                                            {product.price && (
                                                <span className="bg-[rgba(255,255,255,0.1)] text-white px-2 py-1 rounded text-[10px]">
                                                    {formatPrice(product.price)}
                                                </span>
                                            )}
                                            {product.product_status && (
                                                <span className={`px-2 py-1 rounded ${
                                                    product.product_status === 'active' ? 'bg-green-500/20 text-green-400' :
                                                    product.product_status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                                                    'bg-red-500/20 text-red-400'
                                                }`}>
                                                    {product.product_status}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    const renderFinancials = () => {
        // Mock financial data for demonstration
        const financialMetrics = [
            { label: 'Total Revenue', value: '$12,450.00', color: '#34C759' },
            { label: 'Gross Sales', value: '$15,230.00', color: '#9932CC' },
            { label: 'Refunds', value: '$1,280.00', color: '#FF3B30' },
            { label: 'Net Sales', value: '$11,170.00', color: '#007AFF' }
        ];

        return (
            <div className="grid gap-4">
                {financialMetrics.map((metric, index) => (
                    <div 
                        key={index}
                        className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-white text-[12px] font-semibold mb-1">
                                    {metric.label}
                                </h3>
                                <p className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                    Last 30 days
                                </p>
                            </div>
                            <div className="text-right">
                                <span 
                                    className="text-[14px] font-bold"
                                    style={{ color: metric.color }}
                                >
                                    {metric.value}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderAnalytics = () => {
        const analyticsData = [
            { label: 'Units Sold', value: '1,234', trend: '+12%' },
            { label: 'Conversion Rate', value: '3.2%', trend: '+0.8%' },
            { label: 'Avg Order Value', value: '$45.67', trend: '-2.1%' },
            { label: 'Top Product', value: 'Band T-Shirt', trend: '342 sold' }
        ];

        return (
            <div className="grid gap-4">
                {analyticsData.map((item, index) => (
                    <div 
                        key={index}
                        className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-white text-[12px] font-semibold mb-1">
                                    {item.label}
                                </h3>
                                <p className="text-[rgba(255,255,255,0.7)] text-[14px] font-bold">
                                    {item.value}
                                </p>
                            </div>
                            <div className="text-right">
                                <span className={`text-[10px] font-semibold ${
                                    item.trend.startsWith('+') ? 'text-green-400' : 
                                    item.trend.startsWith('-') ? 'text-red-400' : 
                                    'text-[rgba(255,255,255,0.7)]'
                                }`}>
                                    {item.trend}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderOrders = () => {
        return (
            <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">No orders found</h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                    Order data will appear here once you start selling merchandise.
                </p>
            </div>
        );
    };

    const renderContent = () => {
        switch(activeTab) {
            case 'products':
                return renderProducts();
            case 'orders':
                return renderOrders();
            case 'financials':
                return renderFinancials();
            case 'analytics':
                return renderAnalytics();
            default:
                return renderProducts();
        }
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="flex justify-center w-full">
                    <div className="w-[390px] h-[80vh] flex flex-col items-center">
                        <div className="text-white text-left py-[50px]">Loading merch...</div>
                    </div>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Merch</span>
                        <div className="flex items-center gap-4">
                            {selectedItems.length > 0 && canDeleteContent('merch') && (
                                <>
                                    <span className="text-white text-[12px] font-normal">
                                        Selected ({selectedItems.length})
                                    </span>
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={isDeleting}
                                        className="text-red-500 text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50"
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                </>
                            )}
                            
                            {/* Navigation Buttons - Only show when no items are selected */}
                            {selectedItems.length === 0 && (
                                <>
                                    <button 
                                        onClick={() => setShowSearch(!showSearch)}
                                        className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        Search Merch
                                    </button>
                                    
                                    {/* Add Dropdown */}
                                    <div className="relative">
                                        <span 
                                            className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                            onClick={() => setShowImportDropdown(!showImportDropdown)}
                                        >
                                            Add
                                        </span>
                                        
                                        {showImportDropdown && (
                                            <>
                                                {/* Overlay to close dropdown */}
                                                <div 
                                                    className="fixed inset-0 z-10"
                                                    onClick={() => setShowImportDropdown(false)}
                                                />
                                                
                                                {/* Dropdown Menu */}
                                                <div className="absolute left-0 top-6 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg shadow-lg z-20 min-w-[120px]">
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('manual')}
                                                    >
                                                        Manual Add
                                                    </div>
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('bulk')}
                                                    >
                                                        Bulk Import
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                            
                        <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="w-full mb-6">
                        <div className="flex">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                                        activeTab === tab.id 
                                            ? 'text-white' 
                                            : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                                    }`}
                                >
                                    {tab.label}
                                    {activeTab === tab.id && (
                                        <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#9932CC]"></div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search Interface */}
                    {showSearch && (
                        <div className="w-full px-4 mb-6">
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <svg className="w-4 h-4 text-[#9932CC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <span className="text-white text-[12px] font-semibold">Search Merch</span>
                                </div>
                                
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Enter product name, brand, or type..."
                                        className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#9932CC]"
                                    />
                                    
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                
                                {searchQuery && (
                                    <p className="text-[rgba(255,255,255,0.7)] text-[10px] mt-2">
                                        Found {getFilteredMerch().length} product(s) matching "{searchQuery}"
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {renderContent()}
                    </div>

                    {/* Help Text */}
                    <div className="w-full px-4 mt-6 mb-4">
                        <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                            <p>View and manage all your imported merchandise data.</p>
                            <p>Switch between tabs to see products, orders, financials, and analytics.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 relative">
                            <h3 className="text-white text-[16px] font-normal text-left">Delete Selected Merch Items</h3>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                                disabled={isDeleting}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="mb-8">
                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] text-left">
                                Are you sure you want to delete {selectedItems.length} selected merch item(s)? This action cannot be undone.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-4">
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className="flex-1 bg-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Custom Modal */}
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </LayoutHeader>
    );
};

export default ViewAll; 